import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, MapPin, Plus, AlertTriangle, CreditCard, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, offerNameLabel } from "@/lib/api";
import { lookupPincode } from "@/lib/pincode";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";

const EMPTY_ADDR = { address_type: "home", label: "", name: "", mobile: "", line1: "", line2: "", landmark: "", pincode: "", city: "", state: "" };

export default function CheckoutPage() {
  useSeo({ title: "Secure Checkout | Soonyoung", noindex: true, path: "/checkout" });
  const { isAuthed, setLoginOpen, cartData, customer, loadCart } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [service, setService] = useState(null);
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [payMethod, setPayMethod] = useState("razorpay");
  const [preview, setPreview] = useState(null);
  const [placing, setPlacing] = useState(false);

  const items = cartData?.cart?.items || [];

  const loadAddresses = useCallback(async () => {
    try {
      const { data } = await api.get("/ecom/addresses");
      setAddresses(data || []);
      const def = (data || []).find((a) => a.is_default) || data?.[0];
      if (def) setAddressId(def.id);
      if (!data?.length) setShowForm(true);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { if (isAuthed) loadAddresses(); }, [isAuthed, loadAddresses]);

  const runPreview = useCallback(async (addrId, method) => {
    try {
      const addr = addresses.find((a) => a.id === addrId);
      const { data } = await api.post("/ecom/checkout/preview", { address_id: addrId, prepaid: method === "razorpay", ...(addr?.pincode && { pincode: addr.pincode }) });
      setPreview(data);
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; }
  }, [addresses]);

  useEffect(() => {
    if (step >= 2 && addressId) runPreview(addressId, payMethod);
  }, [step, addressId, payMethod, runPreview]);

  const onPincode = async (pin) => {
    setForm((f) => ({ ...f, pincode: pin }));
    if (!/^\d{6}$/.test(pin)) { setService(null); setAreas([]); return; }
    try {
      const { data } = await api.get(`/ecom/catalog/store-for-pincode/${pin}`);
      setService(data);
    } catch { setService(null); }
    try {
      const info = await lookupPincode(pin);
      setAreas(info.areas);
      setForm((f) => ({
        ...f,
        city: info.district,
        state: info.state,
        line2: info.areas.length === 1 ? info.areas[0].name : f.line2,
      }));
    } catch { setAreas([]); }
  };

  const saveAddress = async () => {
    const required = ["name", "mobile", "line1", "pincode"];
    if (required.some((k) => !form[k]?.trim())) { toast.error("Fill name, mobile, address line and pincode"); return; }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    if (service && !service.serviceable) { toast.error(service.message || "Not serviceable here"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/ecom/addresses", {
        ...form,
        label: form.label || null, line2: form.line2 || null, landmark: form.landmark || null,
        city: form.city || null, state: form.state || null,
      });
      toast.success("Address saved");
      setShowForm(false);
      setForm(EMPTY_ADDR);
      await loadAddresses();
      setAddressId(data.id);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const placeOrder = async () => {
    if (preview && !preview.can_checkout) { toast.error("Resolve the blockers before paying"); return; }
    const fresh = await runPreview(addressId, payMethod);
    if (fresh && !fresh.can_checkout) { toast.error("Something changed — review the blockers"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/ecom/orders/checkout", { address_id: addressId, payment_method: payMethod });
      await loadCart();
      if (payMethod === "cod") {
        toast.success(`Order ${data.order.order_number} placed — pay on delivery`);
        navigate(`/orders/${data.order.id}?status=success`);
      } else {
        await openLiveRazorpay(data.razorpay, data.order);
      }
    } catch (e) { toast.error(errMsg(e)); }
    setPlacing(false);
  };

  const verifyAndFinish = async (orderId, resp) => {
    try {
      await api.post(`/ecom/orders/${orderId}/verify-payment`, resp);
      toast.success("Payment verified");
      navigate(`/orders/${orderId}?status=success`);
    } catch {
      toast.error("Payment received but verification is pending — your order is safe");
      navigate(`/orders/${orderId}?status=pending`);
    }
  };

  const openLiveRazorpay = async (payload, order) => {
    const ok = await new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
    const key = payload?.key_id || payload?.key;
    if (!ok || !window.Razorpay || !key) {
      toast.error("Payment gateway unavailable — your order is saved as awaiting payment");
      navigate(`/orders/${order.id}?status=pending`);
      return;
    }
    const rzp = new window.Razorpay({
      key,
      amount: payload?.amount ?? Math.round(order.grand_total * 100),
      currency: payload?.currency || "INR",
      name: "Soonyoung",
      description: `Order ${order.order_number}`,
      order_id: payload?.order_id || payload?.razorpay_order_id || payload?.id,
      prefill: {
        name: customer?.name || payload?.customer?.name || "",
        contact: customer?.mobile || payload?.customer?.contact || "",
        email: customer?.email || payload?.customer?.email || "",
      },
      theme: { color: "#e91e8c" },
      handler: (resp) => verifyAndFinish(order.id, resp),
      modal: {
        ondismiss: () => {
          toast("Payment cancelled — your order is saved as awaiting payment");
          navigate(`/orders/${order.id}?status=pending`);
        },
      },
    });
    rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed — try again"));
    rzp.open();
  };

  if (!isAuthed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="checkout-login-prompt">
        <h1 className="font-display text-3xl text-cream">Sign in to check out</h1>
        <p className="mt-2 text-sm text-sage">One OTP and you're through.</p>
        <button data-testid="checkout-login-btn" onClick={() => setLoginOpen(true)} className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
      </div>
    );
  }

  if (items.length === 0 && step === 1) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-3xl text-cream">Your bag is empty</h1>
        <Link to="/shop" className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Shop First</Link>
      </div>
    );
  }

  const STEPS = ["Address", "Review", "Pay"];

  return (
    <section data-testid="checkout-page" className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Secure Checkout</p>
      <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">Almost yours</h1>

      <div className="mt-8 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold ${step > i ? "bg-gold text-forest" : "border border-champagne/50 text-fern"}`} data-testid={`step-indicator-${i + 1}`}>
              {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`font-mono text-[9px] uppercase tracking-[0.2em] ${step > i ? "text-cream" : "text-fern"}`}>{s}</span>
            {i < 2 && <span className="h-px flex-1 bg-champagne/40" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="checkout-step-address">
          <div className="grid gap-4 sm:grid-cols-2">
            {addresses.map((a) => (
              <button
                key={a.id}
                data-testid={`address-card-${a.id}`}
                onClick={() => setAddressId(a.id)}
                className={`rounded-2xl border-2 p-5 text-left transition-colors ${addressId === a.id ? "border-gold bg-moss" : "border-champagne/40 bg-pine hover:border-gold/50"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{a.address_type}{a.is_default ? " · default" : ""}</span>
                  {addressId === a.id && <Check className="h-4 w-4 text-gold" />}
                </div>
                <p className="mt-2 font-display text-lg text-cream">{a.name}</p>
                <p className="mt-1 text-xs leading-relaxed text-sage">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city} {a.pincode}, {a.state}</p>
                <p className="mt-1 font-mono text-[10px] text-fern">+91 {a.mobile}</p>
              </button>
            ))}

            <button
              data-testid="add-address-toggle"
              onClick={() => setShowForm((s) => !s)}
              className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gold/40 text-gold transition-colors hover:bg-gold/5"
            >
              <Plus className="h-5 w-5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Add new address</span>
            </button>
          </div>

          {showForm && (
            <div className="mt-6 rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="address-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="addr-name" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                <input data-testid="addr-mobile" placeholder="Mobile *" maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                <input data-testid="addr-line1" placeholder="Flat / House / Street *" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none sm:col-span-2" />
                <input data-testid="addr-line2" placeholder="Area / Locality" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                <input data-testid="addr-landmark" placeholder="Landmark" value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                <input data-testid="addr-pincode" placeholder="Pincode *" maxLength={6} value={form.pincode} onChange={(e) => onPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="addr-city" placeholder="District / City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                  <input data-testid="addr-state" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                </div>
                {areas.length > 0 && (
                  <div className="sm:col-span-2">
                    {areas.length > 1 ? (
                      <select
                        data-testid="addr-area-select"
                        value={form.line2}
                        onChange={(e) => setForm({ ...form, line2: e.target.value })}
                        className="w-full rounded-xl border border-gold/50 bg-forest px-4 py-3 text-sm text-cream focus:border-gold focus:outline-none"
                      >
                        <option value="">Select your area — {areas.length} areas in {form.pincode} *</option>
                        {areas.map((a) => (
                          <option key={a.name} value={a.name}>{a.name}{a.delivery ? "" : " (non-delivery office)"}</option>
                        ))}
                      </select>
                    ) : (
                      <p data-testid="addr-area-single" className="rounded-xl bg-moss px-4 py-3 text-sm text-cream">Area: <b>{areas[0].name}</b></p>
                    )}
                    <p data-testid="addr-area-info" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">
                      {(areas.find((a) => a.name === form.line2)?.taluk || areas[0].taluk || "—")} · {form.city || areas[0].district} · {form.state || areas[0].state}
                    </p>
                  </div>
                )}
                <div className="flex gap-2 sm:col-span-2">
                  {["home", "office", "other"].map((t) => (
                    <button key={t} data-testid={`addr-type-${t}`} onClick={() => setForm({ ...form, address_type: t })} className={`rounded-full border px-5 py-2 font-mono text-[10px] uppercase tracking-widest ${form.address_type === t ? "border-gold bg-gold text-forest" : "border-champagne/50 text-sage"}`}>{t}</button>
                  ))}
                </div>
              </div>
              {service && (
                <p data-testid="addr-service-status" className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 text-xs ${service.serviceable ? "bg-moss text-cream" : "bg-gold/10 text-gold"}`}>
                  {service.serviceable ? <Check className="h-4 w-4 text-gold" /> : <AlertTriangle className="h-4 w-4" />}
                  {service.serviceable ? `Delivery available — served by ${service.store_name}` : (service.message || "Not serviceable yet")}
                </p>
              )}
              <button data-testid="addr-save-btn" onClick={saveAddress} disabled={saving} className="mt-5 rounded-full bg-gold px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-forest disabled:opacity-60">
                {saving ? "Saving…" : "Save Address"}
              </button>
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <button
              data-testid="continue-to-review-btn"
              disabled={!addressId}
              onClick={() => setStep(2)}
              className="flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-50"
            >
              Review Order <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}

      {step >= 2 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="checkout-step-review">
          <div className="space-y-6">
            {preview?.serving_store && (
              <p className="flex items-center gap-2 rounded-2xl border border-champagne/40 bg-pine px-5 py-4 text-xs text-sage">
                <MapPin className="h-4 w-4 text-gold" /> Fulfilled by <b className="text-cream">{preview.serving_store.store_name}</b>
              </p>
            )}

            {(() => {
              const a = addresses.find((x) => x.id === addressId);
              if (!a) return null;
              return (
                <div className="flex items-start justify-between gap-4 rounded-2xl border border-champagne/40 bg-pine px-5 py-4" data-testid="review-address">
                  <div className="text-xs text-sage">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Deliver to</p>
                    <p className="mt-1.5 text-sm font-bold text-cream">{a.name} · {a.mobile}</p>
                    <p className="mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}{a.landmark ? `, ${a.landmark}` : ""}</p>
                    <p>{a.city}, {a.state} — {a.pincode}</p>
                  </div>
                  <button data-testid="review-change-address" onClick={() => setStep(1)} className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">Change</button>
                </div>
              );
            })()}

            {preview?.issues?.stock_issues?.length > 0 && (
              <div className="rounded-2xl border border-gold/40 bg-gold/10 p-5" data-testid="stock-issues">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"><AlertTriangle className="h-4 w-4" /> Stock changed</p>
                <ul className="mt-2 space-y-1 text-xs text-cream">
                  {preview.issues.stock_issues.map((s) => <li key={s.product_id}>{s.name} — only {s.available} available (you asked {s.requested})</li>)}
                </ul>
              </div>
            )}
            {preview?.issues?.price_changes?.length > 0 && (
              <div className="rounded-2xl border border-gold/40 bg-gold/10 p-5" data-testid="price-changes">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"><AlertTriangle className="h-4 w-4" /> Prices updated</p>
                <ul className="mt-2 space-y-1 text-xs text-cream">
                  {preview.issues.price_changes.map((s) => <li key={s.product_id}>{s.name}: {inr(s.old_price)} → {inr(s.new_price)}</li>)}
                </ul>
              </div>
            )}

            <ul className="divide-y divide-champagne/30 rounded-3xl border border-champagne/40 bg-pine px-5" data-testid="review-items">
              {(preview?.items || items).map((it, i) => (
                <li key={it.product_id || it.id || i} className="flex items-center justify-between gap-4 py-4">
                  <div>
                    <p className="font-display text-base text-cream">{it.name}</p>
                    <p className="font-mono text-[10px] text-fern">Qty {it.qty}</p>
                    {it.offer_name && (
                      <p data-testid={`review-offer-${i}`} className="mt-1 inline-flex w-fit items-center rounded-full bg-black px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">
                        Offer · {offerNameLabel(it.offer_name)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-cream">{inr(it.line_total)}</p>
                    {it.list_price > it.selling_price && (
                      <p className="font-mono text-[10px] text-fern line-through">{inr(it.list_price * it.qty)}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                data-testid="pay-method-razorpay"
                onClick={() => setPayMethod("razorpay")}
                className={`flex items-center gap-3 rounded-2xl border-2 p-5 text-left transition-colors ${payMethod === "razorpay" ? "border-gold bg-moss" : "border-champagne/40 hover:border-gold/50"}`}
              >
                <CreditCard className="h-5 w-5 text-gold" />
                <div>
                  <p className="text-sm font-bold text-cream">Razorpay</p>
                  <p className="text-[11px] text-fern">UPI · Cards · Netbanking</p>
                </div>
              </button>
              <button
                data-testid="pay-method-cod"
                onClick={() => setPayMethod("cod")}
                className={`flex items-center gap-3 rounded-2xl border-2 p-5 text-left transition-colors ${payMethod === "cod" ? "border-gold bg-moss" : "border-champagne/40 hover:border-gold/50"}`}
              >
                <Banknote className="h-5 w-5 text-gold" />
                <div>
                  <p className="text-sm font-bold text-cream">Cash on Delivery</p>
                  <p className="text-[11px] text-fern">Pay when it arrives</p>
                </div>
              </button>
            </div>
          </div>

          <aside className="h-fit rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="review-summary">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Final Totals</p>
            <div className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between text-sage"><span>MRP total</span><span>{inr(preview?.totals?.subtotal_mrp)}</span></div>
              {(preview?.totals?.product_discount || 0) - (preview?.totals?.offer_discount || 0) > 0 && <div className="flex justify-between text-gold"><span>Product savings</span><span>-{inr((preview?.totals?.product_discount || 0) - (preview?.totals?.offer_discount || 0))}</span></div>}
              {preview?.totals?.offer_discount > 0 && <div className="flex justify-between font-bold text-gold" data-testid="offer-savings-line"><span>Offer savings</span><span>-{inr(preview.totals.offer_discount)}</span></div>}
              {preview?.totals?.coupon_discount > 0 && <div className="flex justify-between text-gold"><span>Coupon {preview?.coupon?.code}</span><span>-{inr(preview.totals.coupon_discount)}</span></div>}
              {preview?.totals?.prepaid_discount > 0 && <div className="flex justify-between text-gold"><span>Prepaid bonus</span><span>-{inr(preview.totals.prepaid_discount)}</span></div>}
              {preview?.coupon?.error && <p className="rounded-xl bg-gold/10 px-3 py-2 text-xs text-gold" data-testid="coupon-error-note">{preview.coupon.error}</p>}
              <div className="flex justify-between text-sage" data-testid="shipping-line">
                <span>Shipping <span className="font-mono text-[9px] text-fern">(by delivery pincode)</span></span>
                <span>
                  {(() => {
                    const t = preview?.totals;
                    if (!t) return "…";
                    const src = t.shipping_source;
                    if (src === "free_above" || src === "free_pincode") return <b className="text-gold">FREE</b>;
                    if (src === "disabled") return "Confirmed by store";
                    if (src === "unavailable") return <span className="font-bold text-gold">Unavailable</span>;
                    if (t.shipping_cost == null || /will apply|at final checkout|at dispatch/i.test(t.shipping_note || "")) return "At dispatch";
                    if (t.shipping_cost > 0) return inr(t.shipping_cost);
                    return <b className="text-gold">FREE</b>;
                  })()}
                </span>
              </div>
              <div className="flex justify-between border-t border-champagne/30 pt-3 text-base font-bold text-cream">
                <span>Grand total</span><span data-testid="grand-total">{inr(preview?.totals?.grand_total)}</span>
              </div>
              {preview?.totals?.shipping_note && <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-fern">{preview.totals.shipping_note}</p>}
            </div>

            {preview && !preview.can_checkout && (
              <p className="mt-4 rounded-xl bg-gold/10 px-4 py-3 text-xs text-gold" data-testid="checkout-blockers">
                Checkout blocked: {preview.checkout_blockers?.join(", ")}
              </p>
            )}

            <button
              data-testid="checkout-pay-btn"
              onClick={placeOrder}
              disabled={placing || (preview && !preview.can_checkout)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-50"
            >
              {placing ? "Placing…" : payMethod === "cod" ? `Place COD Order — ${inr(preview?.totals?.grand_total)}` : `Pay ${inr(preview?.totals?.grand_total)}`}
            </button>
            <button data-testid="back-to-address-btn" onClick={() => setStep(1)} className="mt-3 flex w-full items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage hover:text-gold">
              <ArrowLeft className="h-3 w-3" /> Change address
            </button>
          </aside>
        </motion.div>
      )}
    </section>
  );
}
